/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
  RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { ReportsService } from "../../../../services/api/reports";
import { getHubList } from "../../../../services/api/hub";
import { downloadInscanVsAuditReport } from "../../../../utils/downloadUtils";

const InscanVsAuditReport = () => {
  const FILTER_STORAGE_KEY = "INSCAN_VS_AUDIT_REPORT_FILTERS";

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "days"),
    moment()
  ]);
  const [hubList, setHubList] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [hubLoading, setHubLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [reportData, setReportData] = useState([]);
  const [hubUser, setHubUser] = useState();
  const [downloading, setDownloading] = useState(false);
  const [docTypeList] = useState([
    { label: "Dox", value: "DOX" },
    { label: "Non-dox", value: "NON-DOX" }
  ]);
  const [selectedDocTypes, setSelectedDocTypes] = useState([]);
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
  const [headerHubList, setHeaderHubList] = useState([]);
  const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

  // Fetch hubs from header/localStorage
  useEffect(() => {
    try {
      const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userHubs = userFullData?.userInfo?.hubDetails || [];
      if (userHubs?.length > 0) {
        const headerHubs = [
          { label: "All", value: "ALL" },
          ...userHubs.map(hub => ({
            label: hub.hubName,
            value: hub.hubId
          }))
        ];
        setHeaderHubList(headerHubs);
      } else {
        // If no hubs assigned, show all available hubs but without "All" option
        setHeaderHubList([]);
      }
    } catch (e) {
      console.error("Error parsing user hubs for Inscan vs Audit Report", e);
      setHeaderHubList([]);
    }
  }, []);

  const handleMultiSelectChange = (values, setter, options = []) => {
    if (values.includes("ALL")) {
      const isLatestAll = values[values.length - 1] === "ALL";
      if (isLatestAll) {
        // Select all actual values (excluding the "ALL" option itself)
        const allActualValues = options
          .filter(opt => opt.value !== "ALL")
          .map(opt => opt.value);
        setter(allActualValues);
      } else {
        // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
        setter(values.filter(v => v !== "ALL"));
      }
    } else {
      setter(values);
    }
  };

  // Ref to track whether we already set the hub for the current login session
  const hubInitializedRef = useRef(false);

  // Helper function to save filters to sessionStorage
  const saveFiltersToStorage = () => {
    const filters = {
      dateRange: dateRange ? [
        dateRange[0]?.format("YYYY-MM-DD"),
        dateRange[1]?.format("YYYY-MM-DD")
      ] : null,
      selectedHubs,
      selectedDocTypes,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize
      }
    };
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  };

  // Helper function to restore filters from sessionStorage
  const restoreFiltersFromStorage = () => {
    try {
      const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
          setDateRange([
            moment(filters.dateRange[0]),
            moment(filters.dateRange[1])
          ]);
        }
        if (filters.selectedHubs && filters.selectedHubs.length > 0) {
          setSelectedHubs(filters.selectedHubs);
        }
        if (filters.selectedDocTypes && filters.selectedDocTypes.length > 0) {
          setSelectedDocTypes(filters.selectedDocTypes);
        }
        if (filters.pagination) {
          setPagination(prev => ({
            ...prev,
            current: filters.pagination.current || prev.current,
            pageSize: filters.pagination.pageSize || prev.pageSize
          }));
        }
        // Clear the saved filters after restoring
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
        return true;
      }
    } catch (error) {
      console.error("Error restoring filters:", error);
    }
    return false;
  };
  useEffect(() => {
    fetchHubList();
  }, []);

  useEffect(() => {
    const handleHubChanged = () => {
      const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
      const newActiveHubId = activeHub?.hubId;
      setActiveHubId(newActiveHubId || "ALL_HUBS");
      if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
        setSelectedHubs([newActiveHubId]);
      } else {
        setSelectedHubs(["ALL"]);
      }
    };

    window.addEventListener("hubChanged", handleHubChanged);
    return () => window.removeEventListener("hubChanged", handleHubChanged);
  }, []);

  // Set default hub based on logged-in user — always enforced for hub-restricted roles
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
    const userid = JSON.parse(localStorage.getItem("userId") || "{}");
    const role = user?.roles?.[0];
    setHubUser(role);

    const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
    const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

    // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
    const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);

    if (!hubInitializedRef.current && !hasSavedFilters) {
      const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userHubs = userFullData?.userInfo?.hubDetails || [];

      if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
        setSelectedHubs([currentActiveHubId]);
      } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
        setSelectedHubs([]);
      } else {
        setSelectedHubs(["ALL"]);
      }
      hubInitializedRef.current = true;
    }
  }, [headerHubList]); // Depend on headerHubList to ensure we have options available

  useEffect(() => {
    fetchInscanVsAuditReport();
    // eslint-disable-next-line
  }, [selectedHubs, dateRange, pagination.current, pagination.pageSize, selectedDocTypes]);

  const fetchHubList = async () => {
    setHubLoading(true);
    try {
      const hubData = await getHubList();
      setHubList(hubData);
      // Restore filters after hub list is loaded
      restoreFiltersFromStorage();
    } catch (error) {
      message.error("Failed to fetch hub list");
      setHubList([]);
    } finally {
      setHubLoading(false);
    }
  };

  const fetchInscanVsAuditReport = async () => {
    const user = JSON.parse(localStorage.getItem("userinfo"));
    const isHubAdmin = user?.roles?.[0] === "ROLE_HUBADMIN";
    if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1] || (isHubAdmin && selectedHubs.length === 0)) {
      setReportData([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      return;
    }
    setLoading(true);
    try {
      // Resolve Hubs (selectedHubs) for API logic
      const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
      const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
      const resolvedHubIds = selectedHubs?.includes("ALL")
        ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
        : selectedHubs;

      const params = {
        fromDate: dateRange[0].format("YYYY-MM-DD"),
        toDate: dateRange[1].format("YYYY-MM-DD"),
        hubIds: resolvedHubIds,
        ...(selectedDocTypes.length > 0 && { docTypes: selectedDocTypes }),
        page: pagination.current,
        pageSize: pagination.pageSize
      };
      const { data, total, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getInscanVsAuditReport(params);
      setLastsynced(lastRefreshedAt);
      setNextScheduledRefresh(nextScheduledRefresh);
      setReportData(data);
      setPagination(prev => ({ ...prev, total }));
    } catch (error) {
      message.error("Failed to fetch report data");
      setReportData([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleDownloadReport = async () => {
    if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
      message.warning("Please select date range and at least one hub.");
      return;
    }
    setDownloading(true);
    try {
      // Resolve Hubs (selectedHubs) for Download logic
      const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
      const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
      const resolvedHubIds = selectedHubs?.includes("ALL")
        ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
        : selectedHubs;

      const fromDate = dateRange[0].format("YYYY-MM-DD");
      const toDate = dateRange[1].format("YYYY-MM-DD");
      const hubids = resolvedHubIds.join(",");
      const doctypes = selectedDocTypes.length > 0 ? selectedDocTypes.join(",") : undefined;
      await downloadInscanVsAuditReport({ fromDate, toDate, hubids, doctypes });
      message.success("Report downloaded successfully");
    } catch (error) {
      message.error("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  // Defensive handler for calendar date change
  const handleDateRangeChange = (dates) => {
    if (!Array.isArray(dates) || !dates[0] || !dates[1]) {
      setDateRange([]);
    } else {
      setDateRange(dates);
    }
  };

  const handleClickableNavigation = (route, record) => {
    // Save current filters before navigating
    saveFiltersToStorage();

    const operationDate = record.date;
    let hubId = record.hubId;
    const type = record.doctype ? (record.doctype.toLowerCase() === "dox" ? "dox" : "non-dox") : undefined;

    // Fallback: Try to find hubId from hubList if missing in record
    if (!hubId && record.hubName && hubList.length > 0) {
      const foundHub = hubList.find(h => h.label.toLowerCase() === record.hubName.toLowerCase());
      if (foundHub) {
        hubId = foundHub.value;
      }
    }



    if (!hubId) {
      message.error("Hub ID is missing for this record. Cannot navigate.");
      return;
    }

    navigate(route, {
      state: {
        operationDate,
        hubId,
        type
      }
    });
  };

  const columns = [
    {
      title: "No.",
      width: 60,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      fixed: "left",
    },
    { title: "Date", dataIndex: "date", key: "date", sorter: true, width: 110 },
    { title: "Zone", dataIndex: "zone", key: "zone", sorter: true, width: 100 },
    { title: "Hub Name", dataIndex: "hubName", key: "hubName", sorter: true, width: 180 },
    { title: "State", dataIndex: "statename", key: "statename", sorter: true, width: 120 },
    { title: "Document Type", dataIndex: "doctype", key: "doctype", sorter: true, width: 140, render: (text) => text?.toUpperCase() || "-" },
    {
      title: "Total Fm Inscanned",
      dataIndex: "totalFmInscanned",
      key: "totalFmInscanned",
      sorter: true,
      width: 180,
      render: (text, record) => (
        <span
          style={{
            color: text && text > 0 ? "#1890ff" : "inherit",
            cursor: text && text > 0 ? "pointer" : "default",
            textDecoration: text && text > 0 ? "underline" : "none"
          }}
          onClick={() => {
            if (text && text > 0) {
              handleClickableNavigation("/inscanned-clickable-report", record);
            }
          }}
        >
          {text || 0}
        </span>
      )
    },
    {
      title: "Total Audited Shipments",
      dataIndex: "totalAuditedShipments",
      key: "totalAuditedShipments",
      sorter: true,
      width: 200,
      render: (text, record) => (
        <span
          style={{
            color: text && text > 0 ? "#1890ff" : "inherit",
            cursor: text && text > 0 ? "pointer" : "default",
            textDecoration: text && text > 0 ? "underline" : "none"
          }}
          onClick={() => {
            if (text && text > 0) {
              handleClickableNavigation("/total-audited-clickable-report", record);
            }
          }}
        >
          {text || 0}
        </span>
      )
    },
    {
      title: "Not Audited Shipments",
      dataIndex: "notAuditedShipments",
      key: "notAuditedShipments",
      sorter: true,
      width: 180,
      render: (text, record) => (
        <span
          style={{
            color: text && text > 0 ? "#1890ff" : "inherit",
            cursor: text && text > 0 ? "pointer" : "default",
            textDecoration: text && text > 0 ? "underline" : "none"
          }}
          onClick={() => {
            if (text && text > 0) {
              handleClickableNavigation("/not-audited-clickable-report", record);
            }
          }}
        >
          {text || 0}
        </span>
      )
    },
    {
      title: "Booking Weight",
      dataIndex: "bookingWeight",
      key: "bookingWeight",
      sorter: true,
      width: 150,
      render: (text) => text?.toFixed(3) || "0.000"
    },
    {
      title: "Audited Total Weight",
      dataIndex: "auditedTotalWeight",
      key: "auditedTotalWeight",
      sorter: true,
      width: 180,
      render: (text) => text?.toFixed(3) || "0.000"
    },
    {
      title: "Booking Weight of Audited Shipments",
      dataIndex: "bookingWeightOfAuditedShipments",
      key: "bookingWeightOfAuditedShipments",
      sorter: true,
      width: 220,
      render: (text) => text?.toFixed(3) || "0.000"
    }
  ];

  return (
    <div className="report-container">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading title="Inscan vs Audit Report" navigateTo="/reports" newReportUrl="/reports/inscan-vs-audited/">
          <div style={{ paddingTop: "5px" }}>
            <RefreshTimestamp
              lastRefreshedAt={lastsynced}
              nextScheduledRefresh={nextScheduledRefresh}
            />
          </div>
        </Heading>
      </div>
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">From & To Date</div>
            <CalendarComponent
              className="custom-calendar"
              type="range"
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Select Hubs</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="Select Hubs"
              options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
              onChange={(val) => {
                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedHubs}
              allowClear
              mode="multiple"
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Document Types</div>
            <DropDownComponent
              placeholder="Document Types"
              options={docTypeList}
              onChange={setSelectedDocTypes}
              value={selectedDocTypes}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              dropdownStyle={{ maxHeight: 256, overflow: "auto" }}
            />
          </Col>
          <Col xs={24} sm={12} md={24} lg={6}>
            <div className="filter-label" style={{ visibility: "hidden" }}>Action</div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownloadReport}
              disabled={!dateRange[0] || !dateRange[1]}
            >
              Download Report
            </Button>
          </Col>
        </Row>
      </Card>
      <TableComponent
        columns={columns}
        dataSource={reportData}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ y: "calc(100vh - 300px)", x: 1800 }}
        sticky
        rowKey={record => `${record.date}_${record.hubName}_${record.doctype}`}
      />
    </div>
  );
};

export default InscanVsAuditReport; 
